import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import * as Yup from "yup";
import { useFormik } from "formik";

const ChangePasswordModal = ({ isOpen, toggle, onPasswordChange, t }) => {
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Dynamic validation schema using translations
  const getValidationSchema = () => {
    return Yup.object({
      currentPassword: Yup.string().required(
        t("changePassword.modal.validation.currentPasswordRequired")
      ),

      newPassword: Yup.string()
        .min(8, t("changePassword.modal.validation.newPasswordMinLength"))
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
          t("changePassword.modal.validation.newPasswordComplexity")
        )
        .required(t("changePassword.modal.validation.newPasswordRequired"))
        .notOneOf(
          [Yup.ref("currentPassword")],
          t("changePassword.modal.validation.newPasswordDifferent")
        ),

      confirmPassword: Yup.string()
        .required(t("changePassword.modal.validation.confirmPasswordRequired"))
        .oneOf(
          [Yup.ref("newPassword")],
          t("changePassword.modal.validation.passwordsMustMatch")
        ),
    });
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: getValidationSchema(),
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        // Call the password change function passed as prop
        await onPasswordChange({
          password: values.currentPassword,
          newPassword: values.newPassword,
        });
      } catch (error) {
        // Handle errors (e.g., wrong current password)
        if (error.message.includes("current password")) {
          setFieldError(
            "currentPassword",
            t("changePassword.modal.errors.currentPasswordIncorrect")
          );
        } else {
          setFieldError(
            "newPassword",
            error.message ||
              t("changePassword.modal.errors.changePasswordFailed")
          );
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Reset form when modal closes
  const handleModalClose = () => {
    formik.resetForm();
    setShowPasswords({ current: false, new: false, confirm: false });
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleModalClose} centered size="md">
      <ModalHeader toggle={handleModalClose}>
        <i className="ri-lock-password-line me-2"></i>
        {t("changePassword.modal.title")}
      </ModalHeader>

      <ModalBody>
        <Form onSubmit={formik.handleSubmit}>
          {/* Current Password */}
          <FormGroup>
            <Label for="currentPassword">
              {t("changePassword.modal.labels.currentPassword")}{" "}
              <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type={showPasswords.current ? "text" : "password"}
                id="currentPassword"
                name="currentPassword"
                placeholder={t(
                  "changePassword.modal.placeholders.enterCurrentPassword"
                )}
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  formik.touched.currentPassword &&
                  formik.errors.currentPassword
                }
              />
              <InputGroupText
                style={{ cursor: "pointer" }}
                onClick={() => togglePasswordVisibility("current")}
              >
                <i
                  className={`ri-${
                    showPasswords.current ? "eye-off" : "eye"
                  }-line`}
                ></i>
              </InputGroupText>
              {formik.touched.currentPassword &&
                formik.errors.currentPassword && (
                  <FormFeedback>{formik.errors.currentPassword}</FormFeedback>
                )}
            </InputGroup>
          </FormGroup>

          {/* New Password */}
          <FormGroup>
            <Label for="newPassword">
              {t("changePassword.modal.labels.newPassword")}{" "}
              <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type={showPasswords.new ? "text" : "password"}
                id="newPassword"
                name="newPassword"
                placeholder={t(
                  "changePassword.modal.placeholders.enterNewPassword"
                )}
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  formik.touched.newPassword && formik.errors.newPassword
                }
              />
              <InputGroupText
                style={{ cursor: "pointer" }}
                onClick={() => togglePasswordVisibility("new")}
              >
                <i
                  className={`ri-${showPasswords.new ? "eye-off" : "eye"}-line`}
                ></i>
              </InputGroupText>
              {formik.touched.newPassword && formik.errors.newPassword && (
                <FormFeedback>{formik.errors.newPassword}</FormFeedback>
              )}
            </InputGroup>

            {/* Password strength indicator */}
            {formik.values.newPassword && (
              <div className="mt-2">
                <small className="text-muted">
                  {t("changePassword.modal.passwordStrength.label")}
                </small>
                <div className="progress" style={{ height: "4px" }}>
                  <div
                    className={`progress-bar ${
                      formik.values.newPassword.length >= 8 &&
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(
                        formik.values.newPassword
                      )
                        ? "bg-success"
                        : formik.values.newPassword.length >= 6
                        ? "bg-warning"
                        : "bg-danger"
                    }`}
                    style={{
                      width: `${Math.min(
                        (formik.values.newPassword.length / 8) * 100,
                        100
                      )}%`,
                    }}
                  ></div>
                </div>
              </div>
            )}
          </FormGroup>

          {/* Confirm Password */}
          <FormGroup>
            <Label for="confirmPassword">
              {t("changePassword.modal.labels.confirmPassword")}{" "}
              <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type={showPasswords.confirm ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                placeholder={t(
                  "changePassword.modal.placeholders.confirmNewPassword"
                )}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
              />
              <InputGroupText
                style={{ cursor: "pointer" }}
                onClick={() => togglePasswordVisibility("confirm")}
              >
                <i
                  className={`ri-${
                    showPasswords.confirm ? "eye-off" : "eye"
                  }-line`}
                ></i>
              </InputGroupText>
              {formik.touched.confirmPassword &&
                formik.errors.confirmPassword && (
                  <FormFeedback>{formik.errors.confirmPassword}</FormFeedback>
                )}
            </InputGroup>
          </FormGroup>

          {/* Password Requirements */}
          <div className="alert alert-info mt-3">
            <h6 className="alert-heading">
              {t("changePassword.modal.requirements.heading")}
            </h6>
            <ul className="mb-0 small">
              <li>{t("changePassword.modal.requirements.minLength")}</li>
              <li>{t("changePassword.modal.requirements.uppercase")}</li>
              <li>{t("changePassword.modal.requirements.lowercase")}</li>
              <li>{t("changePassword.modal.requirements.number")}</li>
              <li>{t("changePassword.modal.requirements.specialChar")}</li>
              <li>{t("changePassword.modal.requirements.different")}</li>
            </ul>
          </div>
        </Form>
      </ModalBody>

      <ModalFooter>
        <Button
          color="secondary"
          onClick={handleModalClose}
          disabled={formik.isSubmitting}
        >
          {t("changePassword.modal.buttons.cancel")}
        </Button>
        <Button
          color="primary"
          onClick={formik.handleSubmit}
          disabled={!formik.isValid || formik.isSubmitting}
        >
          {formik.isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {t("changePassword.modal.buttons.changingPassword")}
            </>
          ) : (
            <>
              <i className="ri-save-line me-2"></i>
              {t("changePassword.modal.buttons.changePassword")}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default withTranslation()(ChangePasswordModal);
